import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./RejectGiftPage.css";

const RejectGiftPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(false);

  // Fetch gift details on component mount
  useEffect(() => {
    const fetchGiftDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/gifts/code/${code}`
        );

        if (response.data && response.data.gift) {
          setGift(response.data.gift);
        } else {
          setError("Gift not found");
        }
      } catch (error) {
        console.error("Error fetching gift details:", error);
        setError(
          error.response?.data?.message ||
            "Failed to load gift details. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchGiftDetails();
    } else {
      setError("No gift code provided");
      setLoading(false);
    }
  }, [code]);

  // Handle gift rejection
  const handleRejectGift = async () => {
    try {
      setRejecting(true);
      setError("");

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/gifts/reject/${code}`
      );

      setRejected(true);
    } catch (error) {
      console.error("Error rejecting gift:", error);
      setError(
        error.response?.data?.message ||
          "Failed to reject gift. Please try again."
      );
    } finally {
      setRejecting(false);
    }
  };

  // Determine session type for display
  const getSessionTypeDisplay = (giftType) => {
    if (giftType?.includes("shared")) {
      return "Flexible Session Credits";
    }
    return giftType?.includes("sauna") ? "Infrared Sauna" : "Red Light Therapy";
  };

  // Determine gift type for display
  const getGiftTypeDisplay = (giftType) => {
    if (giftType?.includes("shared")) {
      return giftType?.includes("package")
        ? "Flexible Package"
        : "Flexible Drop-In";
    }
    return giftType?.includes("package") ? "Package" : "Drop-in Session";
  };

  // Render loading state
  if (loading) {
    return (
      <div className="reject-gift-page">
        <div className="loading-spinner"></div>
        <p>Loading gift details...</p>
      </div>
    );
  }

  // Render error state
  if (error && !gift) {
    return (
      <div className="reject-gift-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/")} className="primary-button">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Render rejected state
  if (rejected) {
    return (
      <div className="reject-gift-page">
        <div className="success-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z" />
          </svg>
        </div>
        <h1>Gift Successfully Declined</h1>
        <p>The gift has been declined and the sender has been notified.</p>
        <p>Thank you for your response.</p>
        <button onClick={() => navigate("/")} className="primary-button">
          Return to Home
        </button>
      </div>
    );
  }

  // Render gift details and rejection confirmation
  return (
    <div className="reject-gift-page">
      <h1>Decline Gift</h1>
      {gift && (
        <div className="rejection-details">
          <div className="gift-details-card">
            <h2>Gift Details</h2>
            <div className="gift-detail-item">
              <span className="detail-label">Session Type:</span>
              <span className="detail-value">
                {getSessionTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Gift Type:</span>
              <span className="detail-value">
                {getGiftTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Number of Sessions:</span>
              <span className="detail-value">{gift.sessionCount}</span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">From:</span>
              <span className="detail-value">
                {gift.sender
                  ? `${gift.sender.firstName} ${gift.sender.lastName}`
                  : "A friend"}
              </span>
            </div>
            {gift.note && (
              <div className="gift-detail-item">
                <span className="detail-label">Note:</span>
                <p className="gift-note">{gift.note}</p>
              </div>
            )}
          </div>

          <div className="rejection-confirmation">
            <h3>Are you sure you want to decline this gift?</h3>
            <p>If you decline this gift:</p>
            <ul>
              <li>The gift sender will be notified</li>
              <li>Any account created for this gift will be removed</li>
              <li>This action cannot be undone</li>
            </ul>

            <div className="rejection-actions">
              <button
                onClick={handleRejectGift}
                className="reject-button"
                disabled={rejecting}
              >
                {rejecting ? "Declining..." : "Yes, Decline Gift"}
              </button>
              <button onClick={() => navigate("/")} className="cancel-button">
                No, Keep Gift
              </button>
            </div>

            {error && <p className="error-message">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default RejectGiftPage;
